import {useEffect, useMemo} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import _ from "lodash"
import {Col, Row} from "antd";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {StatusLabel} from "~/components";
import {
	InputPriceField,
	SelectField,
	GroupCheckBoxField,
	GroupRadioField,
	SelectUserAll,
	DateField,
	TextAreaField
} from "~/components/Forms";
import {useUtilities} from "~/hooks";

const paymentOptions = [
	{ value: 'cod', label: 'Tiền mặt'},
	{ value: 'bank', label: 'Chuyển khoản'},
	{ value: 'momo', label: 'Ví Momo'},
	{ value: 'swipe', label: 'Pos'},
];

function ExpenseFormAddEdit({item, listGroup, onHandleSubmit}) {

	const isEdit = Boolean(item?.id);

	const {utilities} = useUtilities();

	const companyOptions = useMemo(() => {
		return Object.entries(utilities.company).map(([key, value]) => ({
			value: key,
			label: <StatusLabel small type={value.color}>{value.title}</StatusLabel>
		}));
	}, [utilities.company]);

	const userOptions = (isEdit && typeof item.user?.id != "undefined") ? [{ label : `${item.user.username} - ${item.user.firstname} ${item.user.lastname}`, value : item.user.id }] : [];

	const groupOptions = [];

	if(!_.isEmpty(listGroup)) {
		if(Object.keys(listGroup).length !== 0) {
			for (const [, value] of Object.entries(listGroup)) {
				groupOptions.push({value : value.id, label: value.name})
			}
		}
	}

	const costTypeOptions = [
		{ value: 'fixed', label: 'Cố định' },
		{ value: 'incidental', label: 'Phát sinh' },
		{ value: 'service', label: 'Dịch vụ' },
		{ value: 'diplomatic', label: 'Ngoại giao' },
		{ value: 'bonus', label: 'Thưởng' },
		{ value: 'other', label: 'Khác' },
	];

	const bankAccountOptions = useMemo(() => {
		return Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({
			value: key,
			label: value.title,
		}));
	}, [utilities.bankAccounts]);

	const initialValues = {
		categoryId: '',
		companyIds: [],
		company: undefined,
		time: new Date(),
		price: 0,
		userId: '',
		note:'',
		paymentMethod: 'cod',
		costType: 'incidental',
		bankAccount: '',
	}

	const validationSchema = Yup.object().shape({
		categoryId: Yup.string().required('Bạn chưa chọn loại thu chi'),
		time: Yup.string().required('Bạn chưa chọn ngày chi'),
		price: Yup.number().required('Giá trị chi không được để trống'),
	});

	const { control, handleSubmit, formState: { isSubmitting, errors }, reset } = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const paymentMethod = useWatch({ control, name: 'paymentMethod' });

	useEffect(() => {
		if(item?.id) {
			initialValues.categoryId = item.categoryId;
			initialValues.company = item.company ? String(item.company) : undefined;
			initialValues.time = item.time*1000;
			initialValues.price = item.price;
			initialValues.userId = item.userId;
			initialValues.note = item.note;
			initialValues.paymentMethod = item.paymentMethod || 'cod';
			initialValues.costType = item.costType || 'incidental';
			initialValues.bankAccount = item.bankAccount || '';
		}
		reset(initialValues);
	}, [item]);

	const onSubmit = async (data) => {
		await onHandleSubmit(data, isEdit);
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Thông tin</p>
			<Controller control={control} name="categoryId" render={({ field }) => (
				<SelectField label="Loại chi" options={groupOptions} errors={errors} {...field}/>
			)}/>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="price" render={({field}) => (
						<InputPriceField label="Giá trị chi" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="time" render={({ field }) => (
						<DateField label="Ngày chi" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>

			{!isEdit && (
				<>
					<p className="heading">Công ty</p>
					<Controller control={control} name="companyIds" render={({ field }) => (
						<GroupCheckBoxField
							name="companyIds"
							label="Chọn công ty (giá trị sẽ chia đều nếu chọn nhiều)"
							options={companyOptions}
							errors={errors}
							value={field.value}
							onChange={field.onChange}
						/>
					)}/>
				</>
			)}
			<p className="heading">Loại chi phí</p>
			<Controller control={control} name="costType" render={({ field }) => (
				<GroupRadioField label="Loại chi phí" options={costTypeOptions} errors={errors} {...field}/>
			)}/>
			<p className="heading">Thanh toán</p>
			<Controller control={control} name="paymentMethod" render={({ field }) => (
				<GroupRadioField label="Phương thức thanh toán" options={paymentOptions} errors={errors} {...field}/>
			)}/>
			{paymentMethod === 'bank' && (
				<Controller control={control} name="bankAccount" render={({ field }) => (
					<SelectField label="Tài khoản ngân hàng" options={bankAccountOptions} errors={errors} {...field}/>
				)}/>
			)}
			<p className="heading">Đối tượng thu chi</p>
			<Controller control={control} name="userId" render={({ field }) => (
				<SelectUserAll options={userOptions} errors={errors} {...field} />
			)}/>
			<Controller control={control} name="note" render={({ field }) => (
				<TextAreaField label="Ghi chú" errors={errors} {...field} />
			)}/>
			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm'} phiếu chi</Button>
			</div>
		</form>
	)
}

export default ExpenseFormAddEdit;
